import PropTypes from 'prop-types';
import { stringify } from 'querystring';
import React, { Component } from 'react';
import { InView } from 'components/InView';

const CONTENTFUL_REGEXP = new RegExp('^((https?://)|(//))?images.ctfassets');

/**
 * Note: This component uses inline styles to
 * ensure that we never display a low-res image
 * that hasn't been blurred to customers.
 */
const styles = {
  default: {
    filter: 'blur(0)',
    transition: '0.2s filter linear',
  },
  preview: {
    filter: 'blur(11px)',
    transition: '0.2s filter linear',
  },
};

/**
 * This logic serves three purposes:
 * 1) It allows each server-rendered instance of DeferredImage to
 *    register an image loader for the full-size image, to be called
 *    when the page has fully loaded.
 * 2) If the load event happens before the component has registered
 *    a listener for it, it will still know to load the full-size image.
 * 3) If the component wants to show a preview image after the page has
 *    already loaded (such as during client-side navigation), we can
 *    circumvent the preview logic altogether.
 */
let windowLoaded = false;
export const windowOnLoadHandlers = [];

const addWindowOnLoadHandler = handler => __CLIENT__ && windowOnLoadHandlers.push(handler);

if (__CLIENT__) {
  const onWindowLoad = () => {
    windowLoaded = true;

    while (windowOnLoadHandlers.length) {
      // Remove the first handler in the list and execute it.
      windowOnLoadHandlers.shift()();
    }

    window.removeEventListener('load', onWindowLoad);
  };

  window.addEventListener('load', onWindowLoad);
}

export const IMAGE_STATES = {
  NONE: 'NONE',
  PREVIEW: 'PREVIEW',
  FULL: 'FULL',
};

/**
 * Note: BaseDeferredImage cannot be Pure, since
 * it cannot determine changes in renderProp output
 * itself.
 */
export class BaseDeferredImage extends Component {
  static deriveImageState(props, state) {
    const { disablePreview, inView, url } = props;
    const { isFullImageQueued, isWindowLoaded } = state;

    if (!url || !inView) {
      return IMAGE_STATES.NONE;
    }

    if (disablePreview || !CONTENTFUL_REGEXP.test(url)) {
      return IMAGE_STATES.FULL;
    }

    // If we've already queued up the full image to be fetched in the background,
    // we want to still show the preview image until the full image has loaded
    // even if window is also loaded at this point.
    if (isFullImageQueued) {
      return IMAGE_STATES.PREVIEW;
    }

    if (isWindowLoaded) {
      return IMAGE_STATES.FULL;
    }

    return IMAGE_STATES.PREVIEW;
  }

  static getDerivedStateFromProps(props, state) {
    const newImageState = BaseDeferredImage.deriveImageState(props, state);

    if (newImageState !== state.imageState) {
      return { imageState: newImageState };
    }

    return null;
  }

  state = {
    imageState: null,
    isFullImageQueued: false,
    isWindowLoaded: windowLoaded,
  };

  constructor(...args) {
    super(...args);
    this.getStyle = this.getStyle.bind(this);
    this.getImageUrl = this.getImageUrl.bind(this);

    if (!windowLoaded) {
      addWindowOnLoadHandler(() => this.setState({ isWindowLoaded: true }));
    }
  }

  componentDidMount() {
    if (this.isImageState(IMAGE_STATES.PREVIEW)) {
      this.queueFullImageLoad();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { imageState } = this.state;

    if (imageState !== prevState.imageState && this.isImageState(IMAGE_STATES.PREVIEW)) {
      this.queueFullImageLoad();
    }
  }

  getImageUrl() {
    const { previewSize, previewQuality, url } = this.props;

    if (this.isImageState(IMAGE_STATES.NONE)) {
      return null;
    }

    if (this.isImageState(IMAGE_STATES.PREVIEW)) {
      return `${url}?${stringify({
        q: previewQuality,
        w: previewSize,
        h: previewSize,
      })}`;
    }

    return url;
  }

  getStyle() {
    return this.isImageState(IMAGE_STATES.PREVIEW) ? styles.preview : styles.default;
  }

  isImageState(state) {
    return IMAGE_STATES[state] === this.state.imageState;
  }

  queueFullImageLoad() {
    this.setState({
      isFullImageQueued: true,
    });

    addWindowOnLoadHandler(() => {
      // Unfortunately, this is not testable.
      /* istanbul ignore next */
      Object.assign(new Image(), {
        onload: () =>
          this.setState({
            isFullImageQueued: false,
          }),
        src: this.props.url,
      });
    });
  }

  render() {
    const { children, forwardedRef } = this.props;

    return children({
      imageUrl: this.getImageUrl(),
      style: this.getStyle(),
      ref: forwardedRef,
    });
  }
}

export const DeferredImage = ({ disableDeferred, ...extraProps }) => {
  // If we don't care about out-of-view deferral of images,
  // we can just load the base image here as if it were already
  // in view.
  if (disableDeferred) {
    return <BaseDeferredImage {...extraProps} inView />;
  }

  // Otherwise, we have to forward the ref given to us by InView so
  // we can pass it on to the render prop in BaseDeferredImage.
  return (
    <InView triggerOnce rootMargin="50px">
      {({ inView, ref }) => <BaseDeferredImage {...extraProps} inView={inView} forwardedRef={ref} />}
    </InView>
  );
};

DeferredImage.defaultProps = {
  disableDeferred: false,
  disablePreview: false,
  previewSize: 200,
  previewQuality: 40,
};

DeferredImage.propTypes = {
  children: PropTypes.func.isRequired,
  disableDeferred: PropTypes.bool,
  disablePreview: PropTypes.bool,
  previewSize: PropTypes.number,
  previewQuality: PropTypes.number,
  url: PropTypes.string,
};

BaseDeferredImage.propTypes = {
  ...DeferredImage.propTypes,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.elementType })]),
  inView: PropTypes.bool.isRequired,
};
